import React from 'react'
import { useHistory } from 'react-router'
import Header from '../../../../components/Header'
import { InlineButton } from '../../../../components/Button/InlineButton'
import { colors } from '../../../../assets/colors'
import Field from '../../../../components/Field'
import { Button } from '../../../../components/Button'
import Icon from '../../../../components/Icon'
import styled from 'styled-components'
import useDownloadMessages from '../../../shared/hooks/useDownloadMessages'
import { useMemo } from 'react'
import { parseFilename, sortFiles } from '../../../../utils/fileUtils'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${colors.silver};
  height: 100%;
`

const Description = styled.div`
  padding: 15px;
  background-color: ${colors.white};
  margin: 10px;
  border-radius: 8px;
  font-size: 14px;
  color: ${colors.mediumGrey};
  line-height: 1.4;
`

const ButtonContainer = styled.div`
  padding: 0 10px;
  margin-bottom: 10px;
`

const DownloadItem = styled.div`
  background-color: ${colors.white};
  margin: 5px 10px;
  border-radius: 8px;
  padding: 0;
  border: 1px solid ${colors.lightGrey};
`

const DownloadButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${colors.darkBlue};
  padding: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  width: 100%;
  
  &:active {
    opacity: 0.7;
  }
`

const FileName = styled.span`
  flex: 1;
  text-align: left;
  font-size: 13px;
  color: ${colors.darkGrey};
`

const SectionHeader = styled.div`
  padding: 15px;
  font-size: 16px;
  font-weight: 600;
  color: ${colors.darkGrey};
  background-color: ${colors.white};
  margin: 10px;
  border-radius: 8px;
  text-align: center;
`

const DownloadMessagesPage: React.FC = () => {
  const history = useHistory()
  const {
    isLoading,
    downloadLinks,
    isQueueStarted,
    startDownloadQueue,
    checkDownloadLinks,
    downloadFile,
    clearDownloadLinks,
    canRequestDownload
  } = useDownloadMessages()

  const sortedDownloadLinks = useMemo(() => {
    return [...downloadLinks].sort((a, b) => sortFiles(a.filename, b.filename))
  }, [downloadLinks])

  return (
    <Container>
      <Header>
        <Header.SideButtons>
          <InlineButton onClick={() => history.goBack()}>Options</InlineButton>
        </Header.SideButtons>
        <Header.Title>
          <span>Download Messages</span>
        </Header.Title>
        <Header.EndButtons />
      </Header>

      <Description>
        Download all your saved Kate messages as ZIP files. Start the download queue to begin preparing your files, 
        then check the status to see when they&apos;re ready for download.
      </Description>

      {!canRequestDownload && (
        <Description style={{ color: colors.grey, backgroundColor: colors.lightGrey }}>
          Request for download was received. Please wait 24 hours before requesting again.
        </Description>
      )}

      <ButtonContainer>
        <Button 
          type="button" 
          onClick={startDownloadQueue} 
          isLoading={isLoading && !downloadLinks.length}
          disabled={!canRequestDownload}
          style={{ marginBottom: '10px' }}
        >
          Start Download Queue
        </Button>
      </ButtonContainer>

      <ButtonContainer>
        <Button 
          type="button" 
          onClick={checkDownloadLinks} 
          isLoading={isLoading}
          style={{ 
            background: colors.blue,
            color: colors.white,
            marginBottom: '10px'
          }}
        >
          Check Download Status
        </Button>
      </ButtonContainer>

      {downloadLinks.length > 0 && (
        <>
          <SectionHeader>
            Available Downloads ({downloadLinks.length} files)
          </SectionHeader>
          {sortedDownloadLinks.map((link) => (
            <DownloadItem key={link.filename}>
              <DownloadButton onClick={() => downloadFile(link.url, link.filename)}>
                <Icon name="download" fill={colors.darkBlue} width="18px" />
                <FileName>{link.filename}</FileName>
                <Icon name="chevronRight" fill={colors.mediumGrey} width="12px" />
              </DownloadButton>
            </DownloadItem>
          ))}
          <ButtonContainer>
            <Button 
              type="button" 
              onClick={clearDownloadLinks}
              style={{ 
                background: colors.lightGrey, 
                color: colors.mediumGrey,
                marginTop: '10px'
              }}
            >
              Clear Download List
            </Button>
          </ButtonContainer>
        </>
      )}
    </Container>
  )
}

export default DownloadMessagesPage 